/**
 * @fileoverview The ONE client-side sign-in throttle policy, for every login in the ecosystem.
 * @package @puffin-core/form-security
 *
 * 5 failed attempts within 15 minutes → a 15-minute lockout: the enterprise default
 * (GitHub / AWS / Azure). The backend rate limiter is the security boundary; this is instant UI
 * feedback — the submit disables, a countdown shows how long is left, and a warning shows how many
 * attempts remain — so a user stops hammering a form the server is about to refuse.
 *
 * ## It is the DEFAULT, not an opt-in (owner rulings Q-UMB-20 / Q-UMB-21)
 *
 * It lived as a private `MARKETING_RATE_LIMIT_CONFIG` inside login-shell's `MarketingCrossDomainLogin`,
 * the only surface that armed the throttle; Q-UMB-14 exported it and Q-UMB-20 (owner ruling
 * 2026-09-24, "safe default + stated opt-out") made it login-shell's `useLoginForm` DEFAULT, so every
 * `<LoginShell>` and every bare `<LoginForm>` is throttled with no prop at all.
 *
 * ## Why it lives HERE and not in login-shell (LIFE-SHORTLISTED-001 D13, 2026-10-13)
 *
 * login-shell is a WEB package (DOM components, a router), and palm-native's sign-in must run the same
 * rule without loading it. The policy belongs beside the engine it parameterises: login-shell's
 * `useLoginForm` and palm-native's `useLogin` both import it from `@puffin-core/form-security/core`.
 * `tests/cross-app/contracts/loginThrottleOptOuts.contract.test.js` pins both seats.
 *
 * Read with the engine as `useRateLimit(LOGIN_RATE_LIMIT_CONFIG)`, counting FAILURES only
 * (`recordFailure` / `recordSuccess`).
 *
 * @module @puffin-core/form-security/loginRateLimitPolicy
 */

/** @type {Readonly<{maxAttempts: number, windowMs: number, lockoutMs: number}>} */
export const LOGIN_RATE_LIMIT_CONFIG = Object.freeze({
  maxAttempts: 5,
  windowMs: 15 * 60 * 1000,
  lockoutMs: 15 * 60 * 1000,
});

export default LOGIN_RATE_LIMIT_CONFIG;
